import type { IconName } from '../components/Icon'
import { TRANSPORT_DESK_ROLES } from './api/transportBookings'

/**
 * The admin's navigation, as data (M23-T02, ADR-0049).
 *
 * This table is the single place that decides what a role sees. The shell it
 * feeds renders it and nothing else — there are no `user?.role === …` guards in
 * the markup, which is what `AdminLayout.tsx` had five of, spread through the
 * JSX where the nav and the permissions could drift apart unnoticed.
 *
 * **Hiding an item is presentation, not access control.** ADR-0049 is explicit:
 * `ProtectedRoute` and the route guards remain the enforcement, and this table
 * must never become the only thing standing between a role and a page.
 */

/**
 * The roles the admin admits, and no others (M36-T03). `support` is the
 * emergency coordinator (ADR-0065 decision 3). Any other role is signed out
 * by `ProtectedRoute`, never cast to one of these.
 */
export const ADMIN_ROLES = ['hospital_admin', 'amtc_admin', 'support'] as const

export type AdminRole = (typeof ADMIN_ROLES)[number]

export function isAdminRole(role: string): role is AdminRole {
  return (ADMIN_ROLES as readonly string[]).includes(role)
}

/**
 * A role as people read it. `support` is shown by its job, "emergency
 * coordinator" (M36-T03); the others by their name, underscores as spaces.
 */
export function roleLabelFor(role: string): string {
  return role === 'support' ? 'emergency coordinator' : role.replace(/_/g, ' ')
}

/** Where coordinators work: the coordination workspace (M36-T07). */
export const COORDINATION_PATH = '/admin/emergency-coordination'

/** Group headings, in render order. `null` is the first, unlabelled group. */
export const NAV_GROUPS = [
  null,
  'REQUEST',
  'PATIENTS',
  'HOSPITALS',
  'REFERENCE',
] as const

export type NavGroup = (typeof NAV_GROUPS)[number]

export type NavItem = {
  label: string
  /** Route path. A record resolves it per role — see `Medication`. It need
   * name only the roles in `roles`. */
  path: string | Partial<Record<AdminRole, string>>
  icon: IconName
  group: NavGroup
  roles: readonly AdminRole[]
  /** `NavLink`'s `end`, so `/admin` does not stay active on every child route. */
  end?: boolean
}

const BOTH = ['hospital_admin', 'amtc_admin'] as const
const AMTC = ['amtc_admin'] as const
const COORDINATORS = ['support', 'amtc_admin'] as const

export const NAV_ITEMS: readonly NavItem[] = [
  {
    label: 'Dashboard',
    path: '/admin',
    icon: 'nav',
    group: null,
    roles: BOTH,
    end: true,
  },
  {
    // M36-T03: the coordinator's only entry. M36-T07: AMTC admins have it too,
    // since they back coordinators up (escalation, handover).
    label: 'Emergency coordination',
    path: COORDINATION_PATH,
    icon: 'phone',
    group: null,
    roles: COORDINATORS,
  },
  {
    // M40-T12 (ADR-0066 decision 7, owner Q17): the transport desk works as
    // `support`, so this entry is also what lets `support` open the page
    // (`canOpen`). The refunds due inside it are AMTC admins' only.
    label: 'Transport bookings',
    path: '/admin/transport-bookings',
    icon: 'car',
    group: null,
    roles: TRANSPORT_DESK_ROLES,
  },
  {
    // M36-T11: the old call-ahead flow's records, read-only. Named so no one
    // mistakes it for the live queue beside it (Emergency coordination).
    label: 'Emergency (old flow)',
    path: '/admin/emergency-requests',
    icon: 'sos',
    group: null,
    roles: AMTC,
  },

  {
    label: 'Appointments',
    path: '/admin/appointments',
    icon: 'clock',
    group: 'REQUEST',
    roles: BOTH,
  },
  {
    label: 'Diagnostics',
    path: '/admin/service-requests',
    icon: 'flask',
    group: 'REQUEST',
    roles: BOTH,
  },
  {
    // One entry, two destinations. The frames fold these together and they are
    // right to, but the pages are NOT two views of one thing — they are
    // opposite ends of one negotiation: a pharmacy answers "do you have it and
    // at what price", AMTC then picks an answer and sends it to the patient.
    // Neither route moves (ADR-0049 §5).
    label: 'Medication',
    path: {
      hospital_admin: '/admin/medication-requests',
      amtc_admin: '/admin/medication-curation',
    },
    icon: 'pill',
    group: 'REQUEST',
    roles: BOTH,
  },

  {
    label: 'Enquiries',
    path: '/admin/enquiries',
    icon: 'chat',
    group: 'PATIENTS',
    roles: AMTC,
  },
  {
    label: 'Leads',
    path: '/admin/leads',
    icon: 'user',
    group: 'PATIENTS',
    roles: AMTC,
  },

  {
    label: 'Beds',
    path: '/admin/beds',
    icon: 'bed',
    group: 'HOSPITALS',
    roles: BOTH,
  },
  {
    label: 'Admissions',
    path: '/admin/admit',
    icon: 'badge',
    group: 'HOSPITALS',
    roles: BOTH,
  },
  {
    // M35-T05 (ADR-0064): where AMTC checks each hospital's emergency line,
    // address, hours and pin before Emergency Help shows them to patients.
    label: 'Emergency contacts',
    path: '/admin/emergency-contacts',
    icon: 'phone',
    group: 'HOSPITALS',
    roles: AMTC,
  },
  {
    // M38-T07 (ADR-0066): fares, pickup areas, hospital zones, capacity and
    // settings for Medcor Transport. Every service stays unbookable until set.
    label: 'Transport settings',
    path: '/admin/transport-settings',
    icon: 'car',
    group: 'HOSPITALS',
    roles: AMTC,
  },

  {
    label: 'Audit Logs',
    path: '/admin/audit-log',
    icon: 'info',
    group: 'REFERENCE',
    roles: BOTH,
  },
  {
    // M37-T02: what the 90-day deletion clears next. For looking only:
    // nothing on it can stop a deletion (owner ruling 2026-10-24).
    label: 'Due for deletion',
    path: '/admin/due-for-deletion',
    icon: 'clock',
    group: 'REFERENCE',
    roles: AMTC,
  },
]

/** The destination for `item` under `role`. */
export function pathFor(item: NavItem, role: AdminRole): string {
  if (typeof item.path === 'string') return item.path
  const path = item.path[role]
  if (path === undefined) {
    throw new Error(`Nav item ${item.label} has no path for ${role}`)
  }
  return path
}

/** Where `role` lands on signing in. */
export function landingFor(role: AdminRole): string {
  return role === 'support' ? COORDINATION_PATH : '/admin'
}

/**
 * Whether `role` may open `pathname` in the admin.
 *
 * **Only `support` is restricted here**: to its own sidebar entries and its
 * profile. The other two roles keep exactly the behaviour they had before
 * M36-T03, where the server is the enforcement and the sidebar is presentation
 * (ADR-0049). For `support` the redirect is the point, because every other page
 * would only show it a wall of 403s.
 */
export function canOpen(role: AdminRole, pathname: string): boolean {
  if (role !== 'support') return true
  const allowed = [
    ...NAV_ITEMS.filter((i) => i.roles.includes(role)).map((i) =>
      pathFor(i, role),
    ),
    '/admin/profile',
  ]
  return allowed.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

export type NavSection = { group: NavGroup; items: NavItem[] }

/**
 * The sections a role sees, in order, **omitting empty groups entirely** — no
 * heading, no gap. ADR-0049: a hospital admin sees a shorter sidebar, not a
 * sidebar full of locked doors, because a disabled control still advertises a
 * capability and reads as a downgraded account rather than a different job.
 */
export function navForRole(role: AdminRole): NavSection[] {
  return NAV_GROUPS.map((group) => ({
    group,
    items: NAV_ITEMS.filter((i) => i.group === group && i.roles.includes(role)),
  })).filter((section) => section.items.length > 0)
}

/** The nav label for the current path, for the breadcrumb. */
export function sectionLabelFor(
  pathname: string,
  role: AdminRole,
): string | null {
  let best: { label: string; length: number } | null = null
  for (const item of NAV_ITEMS) {
    if (!item.roles.includes(role)) continue
    const path = pathFor(item, role)
    const matches = item.end ? pathname === path : pathname.startsWith(path)
    if (matches && (best === null || path.length > best.length)) {
      best = { label: item.label, length: path.length }
    }
  }
  return best?.label ?? null
}
